import { ApiError } from "@carepass/api-client";
import { useState } from "react";
import { useParams } from "react-router";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { canAccessAdminPage } from "../../app/permissions";
import { useAdminSession } from "../../app/providers";
import { ADMIN_ROUTES_BY_SCREEN, parseAdminEntityId, pathForAdminEntityScreen, pathForAdminScreen } from "../../app/routes";
import { StateNotice } from "../workspace/shared";
import { TransferDetailView } from "./components/TransferDetailView";
import { useTransferDetail } from "./queries";
import { TransferActionDialog } from "./TransferActionDialog";

function detailState(query: ReturnType<typeof useTransferDetail>): "ready" | "loading" | "error" | "not-found" {
  if (query.isPending) return "loading";
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found";
  if (query.isError) return "error";
  return "ready";
}

function TransferDetailPageForTransfer({ transferId }: { readonly transferId: number }) {
  const session = useAdminSession();
  const navigation = useAdminNavigation({ contextKey: "transfers", defaultListPath: `${pathForAdminScreen("09")}?page=0&size=20`, originLabel: "송금 상세" });
  const [retryOpen,setRetryOpen]=useState(false);
  const detail=useTransferDetail(transferId);
  const authorities=session.authSession?.authorities ?? [];
  const canExecute=authorities.includes("TRANSFER_EXECUTE");
  const refresh=async()=>{
    const result=await detail.refetch();
    if(result.error)throw result.error;
    return result.data;
  };

  const relatedAction = (() => {
    const transfer = detail.data;
    if (!transfer) return undefined;
    if (transfer.transactionId && canAccessAdminPage(ADMIN_ROUTES_BY_SCREEN["08"].id, authorities, "operations")) {
      const href = pathForAdminEntityScreen("08", transfer.transactionId);
      return { label: "연결 거래 열기", href, onClick: () => navigation.openRelated(href, "송금 상세") };
    }
    if (transfer.refundId && canAccessAdminPage(ADMIN_ROUTES_BY_SCREEN["23"].id, authorities, "operations")) {
      const href = pathForAdminEntityScreen("23", transfer.refundId);
      return { label: "연결 환불 열기", href, onClick: () => navigation.openRelated(href, "송금 상세") };
    }
    if (transfer.benefitClaimId && canAccessAdminPage(ADMIN_ROUTES_BY_SCREEN["24"].id, authorities, "operations")) {
      const href = pathForAdminEntityScreen("24", transfer.benefitClaimId);
      return { label: "연결 혜택지원 열기", href, onClick: () => navigation.openRelated(href, "송금 상세") };
    }
    return undefined;
  })();

  return (
    <>
    <TransferDetailView
      key={transferId}
      transfer={detail.data}
      state={detailState(detail)}
      canExecute={canExecute}
      unresolved={false}
      relatedAction={relatedAction}
      memberHref={detail.data && authorities.includes("MEMBER_READ") ? pathForAdminEntityScreen("06", detail.data.memberId) : undefined}
      onOpenMember={detail.data && authorities.includes("MEMBER_READ") ? () => navigation.openRelated(pathForAdminEntityScreen("06", detail.data!.memberId), "송금 상세") : undefined}
      onBack={navigation.backToList}
      backHref={navigation.backPath}
      backLabel={navigation.backLabel}
      detailHref={pathForAdminEntityScreen("10", transferId)}
      onRetry={() => { void detail.refetch(); }}
      onRefresh={refresh}
      onRequestRetry={()=>setRetryOpen(true)}
      onCommand={async()=>{throw new Error("송금 요청은 공통 확인 창에서만 처리합니다.");}}
    />
    {retryOpen?<TransferActionDialog transferId={transferId} kind="retry" onClose={()=>setRetryOpen(false)}/>:null}
    </>
  );
}

export function TransferDetailPage() {
  const { transferId: rawTransferId } = useParams();
  const transferId = parseAdminEntityId(rawTransferId);
  if (transferId === null) return <StateNotice state="not-found" />;
  return <TransferDetailPageForTransfer transferId={transferId} />;
}
